/**
 * ONE canonical derived step state for the whole staff console (fixr4).
 *
 * Round 4 found the same client described three different ways because every
 * surface derived the step state on its own: the matrix from the batched
 * section rows, the rail and workspace from the client catalog plus
 * resource-aware rules, the queue from its own SQL. This module is the single
 * precedence table. The worker computes it for BOTH staff endpoints (batched
 * inputs, never per-client loops) and the SPA computes it from the detail
 * payload through the same functions. Surfaces CONSUME the truth; none of
 * them re-derive it.
 */

import { missingProductionPrerequisite, productionGateForStep, type ProductionMaterial, type ProductionWait } from "./dfy-prerequisites";

export type StaffTruthOwner = "team" | "client" | "alex" | "none";

/**
 * Canonical step status. `done_ahead` is the explicit anomaly for a legacy
 * output that was finished or published before its step unlocked (audit
 * a2-03): the step still sits behind the rail lock, but the client already
 * has a visible version, so calling it plain "locked" or plain "done" both
 * lie. It counts into the "hotové napřed" summary.
 */
export type StaffTruthStatus =
  | "locked"
  | "done_ahead"
  | "blocked"
  | "working"
  | "on_deck"
  | "client"
  | "done";

export interface StaffStepTruth {
  key: string;
  status: StaffTruthStatus;
  owner: StaffTruthOwner;
  /** Published version of the step's material the client can see right now. */
  visibleVersion: number | null;
  /** Output done or published before the step unlocked (legacy anomaly). */
  aheadOfUnlock: boolean;
  /** Missing or draft team output, optionally held by an upstream gate. */
  production?: ProductionWait;
}

export interface StaffStepSignals {
  key: string;
  railState: "locked" | "next" | "working" | "done";
  /** Newest version of the step's material regardless of publication. */
  material: { status: string; published: boolean; version: number } | null;
  /** Newest PUBLISHED version of the step's material (client-visible). */
  liveVersion: number | null;
  /** Open podněty (new or in_review) attributed to this step. */
  openFeedback: number;
  /** Latest build or automation run touching this step. */
  runStatus: "running" | "failed" | null;
  /** Step is saved as done but its required outputs are missing. */
  outputsMissing?: boolean;
  /** Stripe step without a connect link: the team must supply it. */
  needsConnectUrl?: boolean;
  /** A machine (Alex) is actively working on this step right now. */
  automationActive?: boolean;
  production?: ProductionWait;
}

export type CanonicalLifecycleState =
  | "failed"
  | "working"
  | "ready_for_partner"
  | "waiting_client"
  | "complete";

export interface CanonicalLifecycle {
  state: CanonicalLifecycleState;
  owner: "partner" | "client" | "alex";
  workState: "partner_action" | "automation" | "waiting_client" | "blocked" | "complete";
  railStatus: "blocked" | "working" | "on_deck" | "client" | "done";
}

/**
 * One precedence table for a lifecycle event everywhere it appears.
 * A newer build owns the event while it is running or failed, even if an
 * older version is technically publishable. That prevents one event from
 * appearing under both "Na tahu já" and "Běží výroba".
 */
export function canonicalLifecycle(input: {
  failed?: boolean;
  working?: boolean;
  readyForPartner?: boolean;
  waitingClient?: boolean;
  complete?: boolean;
}): CanonicalLifecycle {
  if (input.failed) {
    return { state: "failed", owner: "alex", workState: "blocked", railStatus: "blocked" };
  }
  if (input.working) {
    return { state: "working", owner: "alex", workState: "automation", railStatus: "working" };
  }
  if (input.readyForPartner) {
    return { state: "ready_for_partner", owner: "partner", workState: "partner_action", railStatus: "on_deck" };
  }
  if (input.complete) {
    return { state: "complete", owner: "client", workState: "complete", railStatus: "done" };
  }
  return { state: "waiting_client", owner: "client", workState: "waiting_client", railStatus: "client" };
}

/**
 * The single derivation. Precedence, top wins:
 * 1. rail lock (with the done_ahead anomaly when a visible output exists),
 * 2. failed run, 3. running automation, 4. done with missing outputs,
 * 5. open podněty, 6. unpublished material ready for the team,
 * 7. missing Stripe connect link, 8. done, 9. waiting on the client.
 */
export function deriveStepTruth(signals: StaffStepSignals): StaffStepTruth {
  const truth = (
    status: StaffTruthStatus,
    owner: StaffTruthOwner,
    aheadOfUnlock = false
  ): StaffStepTruth => ({
    key: signals.key,
    status,
    owner,
    visibleVersion: signals.liveVersion,
    aheadOfUnlock
  });

  if (signals.railState === "locked") {
    if (signals.liveVersion !== null) return truth("done_ahead", "none", true);
    return truth("locked", "none");
  }
  if (signals.runStatus === "failed") return truth("blocked", "alex");
  if (signals.runStatus === "running" || signals.automationActive) {
    return truth("working", "alex");
  }
  if (signals.railState === "done" && signals.outputsMissing) {
    return truth("blocked", "team");
  }
  if (signals.openFeedback > 0) return truth("on_deck", "team");
  const releasable = signals.material
    && !signals.material.published
    && (signals.material.status === "review" || signals.material.status === "approved");
  if (releasable) return truth("on_deck", "team");
  if (signals.production?.targetState === "draft") {
    return { ...truth("on_deck", "team"), production: signals.production };
  }
  if (signals.needsConnectUrl && signals.railState !== "done") {
    return truth("on_deck", "team");
  }
  // F171: "Napojení plateb" is staff-done. `working` = the team has access to
  // the client's Stripe and owes the setup; `next` stays the client's turn
  // (give access), which the fall-through below already says.
  if (signals.key === "payments" && signals.railState === "working") {
    return truth("on_deck", "team");
  }
  if (signals.railState === "done") return truth("done", "none");
  if (signals.production) {
    return {
      ...truth(signals.production.prerequisite?.state === "failed" ? "blocked" : "on_deck", "team"),
      production: signals.production
    };
  }
  return truth("client", "client");
}

/** Rail steps whose material kinds live in deliverables. */
export const STEP_MATERIAL_KINDS: Record<string, string[]> = {
  strategy: ["audit", "plan"],
  salespage: ["salespage"],
  emails: ["emails"],
  webinar: ["webinar"]
};

/**
 * The rail step a build gate or a deliverable kind belongs to. `audit` and
 * `plan` are two historical names for the ONE merged strategy step, and both
 * appear as gate keys in dfy_build_runs (the runner still reports the legacy
 * gates). Every surface that has to ask "does this run belong to this
 * material" answers it HERE, so a run can never own the step on one surface
 * and be invisible on another.
 */
export function buildStepKey(gateOrKind: string): string {
  return gateOrKind === "audit" || gateOrKind === "plan" ? "strategy" : gateOrKind;
}

/** Every gate key a build run may carry for one rail step. */
export function buildGateKeysForStep(step: string): string[] {
  return step === "strategy" ? ["strategy", "audit", "plan"] : [step];
}

/**
 * The merged strategy is one presentational output. Legacy clients may have
 * both audit and plan rows; prefer the plan and never revive two review gates.
 */
export function materialKindsForStep(
  step: string,
  deliverables?: Array<{ kind?: unknown }>
): string[] {
  const kinds = STEP_MATERIAL_KINDS[step] ?? [];
  if (step === "strategy" && deliverables) {
    if (deliverables.some((row) => String(row.kind) === "plan")) return ["plan"];
    if (deliverables.some((row) => String(row.kind) === "audit")) return ["audit"];
  }
  return kinds;
}

/**
 * Which rail step an open podnět belongs to. A podnět bound to a material
 * lands on that material's step; a podnět with no deliverable comes from beta
 * testing and belongs to the beta chapter (both the testing step and the
 * collection step show it, so both must say the team is on turn; audit
 * a2-02).
 */
export function feedbackStepsForKind(deliverableKind: string | null): string[] {
  if (!deliverableKind) return ["beta", "feedback"];
  const step = buildStepKey(deliverableKind);
  if (step in STEP_MATERIAL_KINDS) return [step];
  return [];
}

/** Facts about one client, shaped identically on the server and in the SPA. */
export interface StaffClientFacts {
  /** Gate availability may include a draft hidden from partner workbench. */
  productionMaterials?: ProductionMaterial[];
  steps: Array<{ key: string; state: "locked" | "next" | "working" | "done" }>;
  deliverables: Array<{
    kind: string;
    status: string;
    published: boolean;
    version: number;
  }>;
  /** Open podněty (status new or in_review) with their material kind. */
  openFeedback: Array<{ deliverableKind: string | null; count?: number }>;
  /** Latest run per gate. */
  runs: Array<{ gateKey: string; status: string }>;
  trialLink: { url?: unknown; bot_username?: unknown; invite_limit?: unknown } | null;
  botTransfer: { state?: unknown } | null;
  stripeConnectUrl: string | null;
}

function newestOf(
  materials: StaffClientFacts["deliverables"],
  kinds: string[]
): { newest: StaffClientFacts["deliverables"][number] | null; live: number | null } {
  const rows = materials
    .filter((row) => kinds.includes(row.kind))
    .sort((a, b) => b.version - a.version);
  const live = rows.find((row) => row.published);
  return { newest: rows[0] ?? null, live: live ? live.version : null };
}

function runStatusForStep(facts: StaffClientFacts, step: string): "running" | "failed" | null {
  const run = facts.runs.find((row) => buildStepKey(row.gateKey) === step);
  if (!run) return null;
  return run.status === "running" ? "running" : run.status === "failed" ? "failed" : null;
}

function trimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** Build the canonical signals for one step from the shared fact shape. */
export function stepSignalsFromFacts(facts: StaffClientFacts, stepKey: string): StaffStepSignals {
  const railState = facts.steps.find((step) => step.key === stepKey)?.state ?? "locked";
  // Draft bodies stay hidden from partners, but their state participates in
  // the same newest-material selection as the matrix and launcher.
  const materials = [...facts.deliverables, ...(facts.productionMaterials ?? [])
    .map((row) => ({ ...row, version: row.version ?? 0 }))];
  const kinds = materialKindsForStep(stepKey, materials);
  const { newest, live } = newestOf(materials, kinds);
  const openFeedback = facts.openFeedback
    .filter((row) => feedbackStepsForKind(row.deliverableKind).includes(stepKey))
    .reduce((sum, row) => sum + (row.count ?? 1), 0);
  // Canonical automation = only signals BOTH surfaces can derive (a build run
  // or training in progress). The detail-only automation kinds (Drive
  // provisioning, autolink, prefill) stay workspace presentation; feeding
  // them into the truth made the matrix and the detail disagree again.
  const automationActive = stepKey === "training" && railState === "working";
  const gate = productionGateForStep(stepKey);
  const missing = gate && !newest ? missingProductionPrerequisite(gate, facts.productionMaterials ?? facts.deliverables) : null;
  const prerequisiteStep = missing ? buildStepKey(missing.kind) : null;
  const prerequisiteRun = prerequisiteStep ? runStatusForStep(facts, prerequisiteStep) : null;
  const production: ProductionWait | undefined = gate && newest?.status === "draft" && !newest.published
    ? { targetState: "draft", prerequisite: null }
    : gate && !newest ? {
    prerequisite: missing && prerequisiteStep ? {
      step: prerequisiteStep,
      state: prerequisiteRun === "failed" ? "failed" : prerequisiteRun === "running" ? "working" : missing.reason
    } : null
  } : undefined;

  let outputsMissing = false;
  if (stepKey === "training" && railState === "done") {
    const hasBotLink = trimmedString(facts.trialLink?.url).length > 0
      || trimmedString(facts.trialLink?.bot_username).length > 0;
    outputsMissing = !hasBotLink;
  }
  // F101: a finished handover CLOSES beta and feedback (S20), so after it
  // neither may report missing outputs - "po predani se nesmi tvarit jako
  // nedodelane". Before the handover the check stands: a beta saved done
  // without invites or a transferred bot is still the team's problem.
  if ((stepKey === "beta" || stepKey === "feedback") && railState === "done"
    && !facts.steps.some((step) => step.key === "handover" && step.state === "done")) {
    const outputsReady = Number(facts.trialLink?.invite_limit ?? 0) > 0
      && facts.botTransfer?.state === "transferred";
    outputsMissing = !outputsReady;
  }

  return {
    key: stepKey,
    railState,
    material: newest,
    liveVersion: live,
    openFeedback,
    runStatus: runStatusForStep(facts, stepKey),
    outputsMissing,
    needsConnectUrl: stepKey === "stripe" && !facts.stripeConnectUrl,
    automationActive,
    production
  };
}

/** Canonical truth for every step of the client, in rail order. */
export function deriveClientTruth(facts: StaffClientFacts): StaffStepTruth[] {
  return facts.steps.map((step) => deriveStepTruth(stepSignalsFromFacts(facts, step.key)));
}

/**
 * Who is on turn for the client as a whole: any team-owned step wins, then a
 * machine at work, otherwise the client. The matrix owner pill, the queue
 * grouping and the hero must all read this one answer.
 */
export function clientTruthOwner(truths: StaffStepTruth[]): "partner" | "client" | "alex" {
  if (truths.some((truth) => truth.owner === "team")) return "partner";
  if (truths.some((truth) => truth.owner === "alex")) return "alex";
  return "client";
}

/** How many steps wait on the team right now (the queue's "Na tahu já"). */
export function teamOpenCount(truths: StaffStepTruth[]): number {
  return truths.filter((truth) => truth.owner === "team").length;
}

/**
 * Loosely-typed staff detail payload (the SPA's StaffDetail and the raw JSON
 * of GET /api/staff/clients/:id share this shape).
 */
export interface StaffDetailLike {
  productionMaterials?: ProductionMaterial[];
  steps: Array<{ key: string; state: "locked" | "next" | "working" | "done" }>;
  deliverables: Array<Record<string, unknown>>;
  feedback: Array<Record<string, unknown>>;
  dfyRuns: Array<{ gateKey: string; status: string }>;
  resources: Array<Record<string, unknown>>;
  activeAutomationKinds?: string[];
}

function resourceValue(
  resources: StaffDetailLike["resources"],
  key: string
): Record<string, unknown> | null {
  const row = resources.find((item) => String(item.key) === key);
  const value = row?.value;
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

/**
 * The SPA-side fact builder: the same StaffClientFacts the worker assembles
 * from its batched queries, read out of one detail payload. Keeping both
 * builders next to deriveStepTruth is what makes the surfaces agree.
 */
export function factsFromStaffDetail(detail: StaffDetailLike): StaffClientFacts {
  const stripeSetup = resourceValue(detail.resources, "stripe_setup");
  const connectUrl = typeof stripeSetup?.connect_url === "string"
    ? stripeSetup.connect_url.trim()
    : "";
  return {
    productionMaterials: detail.productionMaterials,
    steps: detail.steps.map((step) => ({ key: step.key, state: step.state })),
    deliverables: detail.deliverables.map((row) => ({
      kind: String(row.kind ?? ""),
      status: String(row.status ?? ""),
      published: row.published === 1 || row.published === true,
      version: Number(row.version ?? 0)
    })),
    openFeedback: detail.feedback
      .filter((row) => {
        const status = String(row.status ?? "");
        return status === "new" || status === "in_review";
      })
      .map((row) => ({
        deliverableKind: typeof row.deliverableKind === "string" && row.deliverableKind
          ? row.deliverableKind
          : null
      })),
    runs: detail.dfyRuns.map((run) => ({ gateKey: run.gateKey, status: run.status })),
    trialLink: resourceValue(detail.resources, "trial_link"),
    botTransfer: resourceValue(detail.resources, "bot_transfer_state"),
    stripeConnectUrl: connectUrl || null
  };
}

/**
 * Display status the staff surfaces render for a canonical truth. `client`
 * keeps the step's own display nuance (testing, review, waiting_apple, ...),
 * everything more specific overrides it, so a contradiction cannot survive.
 */
export function truthDisplayStatus<T extends string>(
  truth: StaffStepTruth,
  fallback: T
): T | StaffTruthStatus {
  return truth.status === "client" ? fallback : truth.status;
}
